import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Gauge, Zap, Fuel, Cog, Palette, ShieldCheck, Check } from "lucide-react";
import { getVehicle, submitTestDrive, resolveImageUrl } from "../lib/api";
import { toast } from "sonner";

const money = (n) => `$${n.toLocaleString()}`;

export default function VehicleDetail() {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", preferred_date: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getVehicle(id).then(setVehicle).catch(() => setVehicle(null));
  }, [id]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitTestDrive({
        ...form,
        vehicle_id: vehicle?.id,
        vehicle_label: `${vehicle?.year} ${vehicle?.make} ${vehicle?.model}`,
      });
      toast.success("Test drive requested. We'll be in touch shortly.");
      setForm({ full_name: "", email: "", phone: "", preferred_date: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!vehicle) {
    return (
      <div className="pt-40 min-h-screen max-w-[1500px] mx-auto px-6 md:px-10">
        <div className="text-white/50">Loading vehicle...</div>
      </div>
    );
  }

  const specs = [
    { icon: Gauge, label: "Mileage", value: `${vehicle.mileage.toLocaleString()} mi` },
    { icon: Zap, label: "Horsepower", value: `${vehicle.horsepower} hp` },
    { icon: Fuel, label: "Fuel", value: vehicle.fuel_type },
    { icon: Cog, label: "Transmission", value: vehicle.transmission },
    { icon: Palette, label: "Exterior", value: vehicle.exterior_color },
    { icon: ShieldCheck, label: "Drivetrain", value: vehicle.drivetrain.toUpperCase() },
  ];

  return (
    <div data-testid="page-vehicle-detail" className="pt-32 pb-24 max-w-[1500px] mx-auto px-6 md:px-10">
      <Link to="/inventory" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-8" data-testid="back-to-inventory">
        <ArrowLeft size={14} /> All inventory
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Image / gallery */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-white/10 bg-[#111]" data-testid="vehicle-main-image">
            {vehicle.images?.[activeImage] && (
              <img
                src={resolveImageUrl(vehicle.images[activeImage])}
                alt={`${vehicle.make} ${vehicle.model}`}
                className="w-full h-full object-cover"
              />
            )}
          </div>
          {vehicle.images?.length > 1 && (
            <div className="grid grid-cols-4 gap-3 mt-3">
              {vehicle.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  className={`aspect-video rounded-lg overflow-hidden border transition-colors ${activeImage === i ? "border-[var(--primary)]" : "border-white/10"}`}
                >
                  <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        {/* Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="overline">{vehicle.year} · {vehicle.body_type}</div>
          <h1 className="font-display text-5xl md:text-7xl mt-3 tracking-tighter leading-none">
            {vehicle.make}
            <br />
            <span className="text-[var(--primary)]">{vehicle.model}</span>
          </h1>
          <div className="mt-8 font-display text-4xl">{money(vehicle.price)}</div>
          <p className="text-white/70 mt-6 leading-relaxed">{vehicle.description}</p>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-8">
            {specs.map((s) => (
              <div key={s.label} className="bento-cell !p-4">
                <s.icon size={14} className="text-[var(--primary)]" />
                <div className="overline text-[10px] mt-2">{s.label}</div>
                <div className="text-sm mt-1 capitalize">{s.value}</div>
              </div>
            ))}
          </div>

          {vehicle.features?.length > 0 && (
            <div className="mt-8">
              <div className="overline mb-3">Features</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {vehicle.features.map((f) => (
                  <div key={f} className="text-sm text-white/70 flex items-center gap-2">
                    <Check size={14} className="text-[var(--primary)]" /> {f}
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Test drive form */}
      <div className="mt-24 grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
        <div>
          <div className="overline">Book It</div>
          <h2 className="font-display text-5xl md:text-6xl mt-3 tracking-tighter">
            Reserve a
            <br />
            test drive.
          </h2>
          <p className="text-white/60 mt-6 max-w-md">
            Schedule a private appointment. Bring a friend, take your time — no pressure, ever.
          </p>
          <div className="mt-8 text-sm text-white/60 space-y-2">
            <div><span className="text-white">VIN:</span> {vehicle.vin}</div>
            <div><span className="text-white">Engine:</span> {vehicle.engine}</div>
            <div><span className="text-white">Interior:</span> {vehicle.interior_color}</div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="glass rounded-2xl p-8 space-y-5" data-testid="test-drive-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label>Full name</label>
              <input required data-testid="td-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            </div>
            <div>
              <label>Email</label>
              <input required type="email" data-testid="td-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label>Phone</label>
              <input required data-testid="td-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <label>Preferred date</label>
              <input required type="date" data-testid="td-date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} />
            </div>
          </div>
          <div>
            <label>Message</label>
            <textarea rows={3} data-testid="td-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          </div>
          <button disabled={submitting} type="submit" className="btn-primary w-full justify-center" data-testid="td-submit">
            {submitting ? "Sending..." : "Request Test Drive"}
          </button>
        </form>
      </div>
    </div>
  );
}
